import React, { useState, useEffect } from 'react';
import { useNavigate } from '../utils/SimpleRouter';
import { ArrowRight, Check, Clock, Shield, Zap, Settings, Image as ImageIcon, Calendar, Filter, Moon, Monitor } from 'lucide-react';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const features = [
    {
      icon: <Zap size={32} />,
      title: "Klar på 1 minutt",
      description: "Ingen komplisert oppsett. Logg inn, last opp innhold og vis det på skjermen med én gang."
    },
    {
      icon: <Shield size={32} />,
      title: "Innebygget ordfilter",
      description: "Automatisk filter stopper upassende ord før de vises. Redaktøren godkjenner alle meldinger."
    },
    {
      icon: <ImageIcon size={32} />,
      title: "Bilder og artikler",
      description: "Del bilder, artikler og viktige meldinger. Perfekt for beboermeldinger og arrangementer."
    },
    {
      icon: <Calendar size={32} />,
      title: "Tidsplanlegging",
      description: "Sett start- og sluttdato på innhold. Automatisk publisering og arkivering."
    },
    {
      icon: <Moon size={32} />,
      title: "Skjermsparer",
      description: "Aktiver skjermsparer med ett klikk for nattetid. Forleng skjermens levetid."
    },
    {
      icon: <Settings size={32} />,
      title: "Enkel administrasjon",
      description: "Brukervennlig admin-panel. Administrer flere skjermer fra én konto."
    }
  ];

  const testimonials = [
    {
      quote: "Perfekt for vårt borettslag! Alle får viktig info på ett sted.",
      author: "Anne-Marie, Styreleder",
      company: "Fjellveien Borettslag"
    },
    {
      quote: "Slapp å printe og henge opp plakater. Alt vises automatisk på skjermen.",
      author: "Per, Vaktmester",
      company: "Osloveien Sameie"
    },
    {
      quote: "Oppsett tok 5 minutter. Nå sparer vi mye tid på kommunikasjon.",
      author: "Linda, Administrator",
      company: "Storgata Boligselskap"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="landing-page">
      {/* Header/Nav */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="logo">
            <img src="/infovegg-logo.svg" alt="Infovegg" className="logo-image" />
          </div>
          <div className="nav-actions">
            <a href="/" target="_blank" rel="noopener noreferrer" className="btn-text" style={{ textDecoration: 'none' }}>Logg inn</a>
            <button className="btn-primary" onClick={() => navigate('/bestill')}>Bestill nå</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Digital infotavle
              <span className="highlight"> for 50,- per oppgang</span>
            </h1>
            <p className="hero-subtitle">
              Superenkel digital kommunikasjon for borettslag og sameier. 
              Både styret og beboerne elsker hvor lett det er å bruke.
            </p>
            <div className="hero-cta">
              <button className="btn-hero" onClick={() => navigate('/bestill')}>
                Prøv gratis i 14 dager
                <ArrowRight size={20} />
              </button>
              <div className="price-badge">
                <span className="price-label">Deretter kun</span>
                <span className="price-amount">50,-/mnd/oppgang*</span>
              </div>
            </div>
            <p className="price-disclaimer">*Alle priser er eks. mva og frakt</p>
            <div className="hero-features">
              <div className="hero-feature-item">
                <Check size={18} className="check-icon" />
                <span>Klar på 1 minutt</span>
              </div>
              <div className="hero-feature-item">
                <Check size={18} className="check-icon" />
                <span>Ingen binding</span>
              </div>
              <div className="hero-feature-item">
                <Check size={18} className="check-icon" />
                <span>Bruk egen skjerm/TV</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-photo">
              <img src="/hero-image.jpg" alt="Infovegg i bruk" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Alt du trenger i én løsning</h2>
            <p>Kraftige funksjoner som gjør kommunikasjon enklere</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="hardware-section">
        <div className="section-container">
          <div className="hardware-content">
            <div className="hardware-text">
              <h2>Bruk din egen TV eller skjerm</h2>
              <p className="hardware-subtitle">
                Har dere allerede en TV eller skjerm? Perfekt! Infovegg fungerer med alle moderne skjermer.
                Ingen dyre innkjøp nødvendig.
              </p>
              <div className="hardware-specs">
                <div className="spec-item">
                  <Monitor size={24} className="spec-icon" />
                  <div>
                    <h4>Fungerer med alle skjermer</h4>
                    <p>TV, dataskjerm eller profesjonell display - alt fungerer</p>
                  </div>
                </div>
                <div className="spec-item">
                  <ImageIcon size={24} className="spec-icon" />
                  <div>
                    <h4>Vil du kjøpe skjerm?</h4>
                    <p>Vi kan levere profesjonelle LG-skjermer fra 5.990,- (valgfritt tillegg)</p>
                  </div>
                </div>
                <div className="spec-item">
                  <Settings size={24} className="spec-icon" />
                  <div>
                    <h4>Lynrask utrulling</h4>
                    <p>Slå på skjermen, ferdig! Vi leverer plug & play skjermer for lynrask utrulling.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="hardware-image">
              <div className="screen-preview">
                <div className="screen-frame"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Kom i gang på 5 minutter</h2>
            <p>Så enkelt at både styret og beboerne mestrer det</p>
          </div>
          <div className="steps-container">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Registrer deg</h3>
              <p>Fyll ut skjema og få 14 dagers gratis prøveperiode</p>
            </div>
            <div className="step-arrow"><ArrowRight size={24} /></div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Opprett passord</h3>
              <p>Du får e-post med lenke for å opprette passord</p>
            </div>
            <div className="step-arrow"><ArrowRight size={24} /></div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Åpne på skjermen</h3>
              <p>Åpne nettleser på din TV/skjerm og logg inn</p>
            </div>
            <div className="step-arrow"><ArrowRight size={24} /></div>
            <div className="step-card">
              <div className="step-number">4</div>
              <h3>Del innhold</h3>
              <p>Legg inn meldinger, bilder og artikler - ferdig!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Hva våre kunder sier</h2>
          </div>
          <div className="testimonials-slider">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`testimonial-card ${index === currentSlide ? 'active' : ''}`}
              >
                <div className="quote-mark">"</div>
                <p className="testimonial-quote">{testimonial.quote}</p>
                <div className="testimonial-author">
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.company}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="slider-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-card">
            <h2>Kun 50,- per måned per oppgang</h2>
            <div className="cta-features">
              <div className="cta-feature">
                <Check size={20} style={{ color: '#50E3C2', flexShrink: 0 }} />
                <span>14 dagers gratis prøveperiode</span>
              </div>
              <div className="cta-feature">
                <Check size={20} style={{ color: '#50E3C2', flexShrink: 0 }} />
                <span>Ingen oppstartskostnader</span>
              </div>
              <div className="cta-feature">
                <Check size={20} style={{ color: '#50E3C2', flexShrink: 0 }} />
                <span>Ingen binding</span>
              </div>
              <div className="cta-feature">
                <Check size={20} style={{ color: '#50E3C2', flexShrink: 0 }} />
                <span>Alle funksjoner inkludert</span>
              </div>
            </div>
            <p className="cta-note">
              Trenger dere skjerm? Vi leverer profesjonelle LG-skjermer fra 5.990,- eks. mva/frakt (valgfritt tillegg)
            </p>
            <button className="btn-cta" onClick={() => navigate('/bestill')}>
              Start gratis prøveperiode
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Hardware Offering */}
      <section className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Trenger dere skjermer?</h2>
            <p>Vi leverer profesjonelle LG-skjermer ferdig konfigurert til plug & play</p>
          </div>
          <div className="hardware-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="hardware-offer-card">
              <h3>32" LG profesjonell skjerm</h3>
              <p className="offer-subtitle">
                Ferdig oppsatt og klar til bruk. Bare heng på veggen, koble til strøm og internett - ferdig!
              </p>
              <div className="offer-features">
                <div className="offer-feature">
                  <Check size={18} className="check-icon" />
                  <span>Designet for 24/7 drift</span>
                </div>
                <div className="offer-feature">
                  <Check size={18} className="check-icon" />
                  <span>Overlegen bildekvalitet</span>
                </div>
                <div className="offer-feature">
                  <Check size={18} className="check-icon" />
                  <span>Monteres stående eller liggende</span>
                </div>
                <div className="offer-feature">
                  <Check size={18} className="check-icon" />
                  <span>Veggfeste inkludert</span>
                </div>
              </div>
              <div className="offer-price">
                <span className="price-label">Fra kun</span>
                <span className="price-large">5.990,-</span>
                <span className="price-suffix">per skjerm (eks. mva/frakt)</span>
              </div>
              <div className="offer-note">
                <p><strong>Andre størrelser?</strong> Vi kan levere mange forskjellige skjermstørrelser etter ønske.</p>
                <p><strong>Miks av orientering?</strong> Vi kan levere både stående og liggende skjermer i samme prosjekt.</p>
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  Ta kontakt for et tilbud tilpasset deres behov.
                </p>
              </div>
            </div>
            <div className="disclaimer-box">
              <strong>Viktig å merke seg:</strong>
              <ul>
                <li>Etablering av strøm og internett til skjermene er ikke inkludert (må gjøres av elektriker)</li>
                <li>Oppmontering på vegg er ikke inkludert</li>
                <li>Veggfeste følger med skjermen</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-content">
            <img src="/infovegg-logo-white.svg" alt="Infovegg" className="footer-logo-img" />
            <p className="footer-tagline">Digital kommunikasjon gjort enkelt</p>
          </div>
          <div className="footer-bottom">
            <p>En tjeneste fra <a href="https://displaysystem.no" target="_blank" rel="noopener noreferrer">Displaysystem.no</a></p>
            <p className="footer-microtag">INFOvegg.no - {new Date().getFullYear()}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;